import type * as Y from 'yjs';

import { getYMutationClock } from './getYMutationClock';

type YJSInput =
    | Y.Map<unknown>
    | Y.Array<unknown>
    | Y.Text
    | Y.XmlText
    | Y.XmlFragment
    | Y.XmlElement;

const cache = new WeakMap<
    YJSInput,
    WeakMap<(args: unknown) => unknown, { current: number; result: any }>
>();

/**
 * Cache a function processing a Yjs input.
 */
export function memoYjsTransform<Input extends YJSInput, Output>(
    transform: (input: Input) => Output
): (input: Input) => Output {
    return (input) => {
        return yjsWithImmutableTransform(input, transform);
    };
}

/**
 * Transform a Yjs input to any output.
 * The transformation is cached, to only be called when the input is mutated.
 * The function `transform` should be a static one, otherwise the cache will be invalidated.
 */
function yjsWithImmutableTransform<Input extends YJSInput, Output>(
    input: Input,
    transform: (input: Input) => Output
): Output {
    const mutation = getYMutationClock(input);
    if (mutation === undefined) {
        return transform(input);
    }

    const fnsCache = cache.get(input) || new WeakMap();
    const cached = fnsCache.get(transform);

    if (cached && cached.current === mutation) {
        return cached.result;
    }

    const result = transform(input);
    cache.set(input, fnsCache);
    fnsCache.set(transform, { current: mutation, result });

    return result;
}
